// @ts-check

import AddFieldsPage from '@/components/pages/envelopes/AddFieldsPage/AddFieldsPage.vue';
import CreatePage from '@/components/pages/envelopes/CreatePage';
import DocumentsPage from '@/components/pages/envelopes/DocumentsPage';
import ExpiryPage from '@/components/pages/envelopes/ExpiryPage';
import FieldsPage from '@/components/pages/envelopes/FieldsPage';
import PartiesPage from '@/components/pages/envelopes/PartiesPage';
import ProgressPage from '@/components/pages/envelopes/ProgressPage';
import RemindersPage from '@/components/pages/envelopes/RemindersPage';
import SharingPage from '@/components/pages/envelopes/SharingPage';
import SigningSequencePage from '@/components/pages/envelopes/SigningSequencePage';
import SummaryPage from '@/components/pages/envelopes/SummaryPage';
import { queryClient } from '@/lib/tanstackQuery';
import { envelopeKeys, queryEnvelopeFields } from '@/queries/envelopes';

const createRoute = {
  /**
   * Step 1 - Select document/s and template/s
   *
   */
  path: '/envelopes/create',
  name: 'EnvelopesCreate',
  component: CreatePage,
  meta: {
    title: 'createEnvelope',
    requiresAuth: true,
  },
};
const stepRoutes = {
  /**
   * StepsPage shows the current "step" with progress bar and back/next buttons
   */
  path: '/envelopes/:fingerprint',
  name: 'EnvelopesSteps',
  component: ProgressPage,
  meta: {
    title: 'createEnvelope',
    requiresAuth: true,
  },
  /**
   * @param {import('vue-router').Location} to
   * @param {import('vue-router').Location} from
   * @param {import('vue-router').NavigationGuardNext} next
   */
  beforeEnter: (to, from, next) => {
    // Replace the "create" step with the "documents" step
    // so the browser back button works like the app back button
    if (from.name === 'EnvelopesCreate') {
      window.history.replaceState(
        {},
        document.title,
        `/envelopes/${to.params?.fingerprint}/documents`,
      );
    }

    next();
  },
  children: [
    {
      /**
       * Step 1 - Configure parties
       */
      path: 'documents',
      name: 'EnvelopesStepDocuments',
      component: DocumentsPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
      props: true,
    },
    {
      /**
       * Step 2 - Configure parties
       */
      path: 'parties',
      name: 'EnvelopesStepParties',
      component: PartiesPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
        keepAlive: true,
      },
      props: true,
    },
    {
      /**
       * Step 3 - Fields
       */
      path: 'fields',
      name: 'EnvelopesStepFields',
      component: FieldsPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
        keepAlive: true,
      },
      props: true,
      /**
       * @param {import('vue-router').Route} to
       * @param {import('vue-router').Route} from
       * @param {Function} next
       */
      beforeEnter: async (to, from, next) => {
        // Fetch prefillable fields for the envelope
        const fields = await queryClient.fetchQuery({
          queryKey: envelopeKeys.fields(to.params.fingerprint),
          queryFn: queryEnvelopeFields,
        });

        if (!fields?.length) {
          // If coming back from AddFields, redirect to Parties
          if (from.name === 'EnvelopesStepAddFields') {
            next({ name: 'EnvelopesStepParties', params: to.params });
          } else {
            next({ name: 'EnvelopesStepAddFields', params: to.params });
          }

          return;
        }

        next();
      },
    },
    {
      /**
       * Step 3 - Fields
       */
      path: 'add-fields',
      name: 'EnvelopesStepAddFields',
      component: AddFieldsPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
        keepAlive: false,
      },
      props: true,
    },
    {
      /**
       * Step 4 - Summary
       */
      path: 'summary',
      name: 'EnvelopesStepSummary',
      component: SummaryPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
      props: true,
    },
    {
      /**
       * Catch all - Steps page will redirect to correct step
       */
      path: '*',
    },
  ],
};

const editRoutes = {
  /**
   * EditPage shows the "step" in an edit state without progress bar
   */
  path: '/envelopes/:fingerprint/edit',
  name: 'EnvelopesEdit',
  component: ProgressPage,
  meta: {
    title: 'createEnvelope',
    requiresAuth: true,
  },
  children: [
    {
      /**
       * Edit SigningSequence
       */
      path: 'signing-sequence',
      name: 'EnvelopesEditSigningSequence',
      component: SigningSequencePage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
    },
    {
      /**
       * Edit Sharing
       */
      path: 'sharing',
      name: 'EnvelopesEditSharing',
      component: SharingPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
    },
    {
      /**
       * Edit Reminders
       */
      path: 'reminders',
      name: 'EnvelopesEditReminders',
      component: RemindersPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
    },
    {
      /**
       * Edit Expiry
       */
      path: 'expiry',
      name: 'EnvelopesEditExpiry',
      component: ExpiryPage,
      meta: {
        title: 'createEnvelope',
        requiresAuth: true,
      },
    },
  ],
};

const routes = [createRoute, editRoutes, stepRoutes];

export default routes;
